import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApisService } from '@core/services/apis.service';

@Component({
  selector: 'app-story-edit',
  templateUrl: './story-edit.component.html'
})
export class StoryEditComponent implements OnInit {
  storyId!: number;
  loading = false;

  title = '';
  content = '';
  status = 'pending';
  coverUrl: string | null = null;

  images: any[] = []; // {id,image,sort_order}
  newCoverFile: File | null = null;
  newImagesFiles: File[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apis: ApisService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.storyId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  load(): void {
    this.loading = true;
    this.apis.getDashboardStoryDetails(this.storyId).subscribe({
      next: (res: any) => {
        const s = res?.data;
        this.title = s?.title || '';
        this.content = s?.content || '';
        this.status = s?.status || 'pending';
        this.coverUrl = s?.cover || null;
        this.images = (s?.images || []).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Failed to load story');
      }
    });
  }

  onPickCover(ev: any): void {
    const f: File | undefined = ev?.target?.files?.[0];
    if (!f) return;
    this.newCoverFile = f;
  }

  onPickImages(ev: any): void {
    const files: FileList | undefined = ev?.target?.files;
    if (!files || files.length === 0) return;
    this.newImagesFiles = Array.from(files);
  }

  moveUp(i: number): void {
    if (i <= 0) return;
    const tmp = this.images[i - 1];
    this.images[i - 1] = this.images[i];
    this.images[i] = tmp;
  }

  moveDown(i: number): void {
    if (i >= this.images.length - 1) return;
    const tmp = this.images[i + 1];
    this.images[i + 1] = this.images[i];
    this.images[i] = tmp;
  }

  removeImage(img: any): void {
    if (!confirm('Delete this image?')) return;
    this.apis.deleteDashboardStoryImage(this.storyId, Number(img.id)).subscribe({
      next: () => {
        this.toastr.success('Image deleted');
        this.images = this.images.filter((x) => x.id !== img.id);
      },
      error: () => this.toastr.error('Failed to delete image')
    });
  }

  saveOrder(): void {
    const ids = this.images.map((x) => Number(x.id));
    this.apis.reorderDashboardStoryImages(this.storyId, ids).subscribe({
      next: (res: any) => {
        this.toastr.success('Images reordered');
        const data = res?.data || [];
        this.images = data.slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
      },
      error: () => this.toastr.error('Failed to reorder images')
    });
  }

  saveText(): void {
    this.loading = true;
    this.apis.updateDashboardStory(this.storyId, { title: this.title, content: this.content, status: this.status }).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success('Story updated');
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Failed to update story');
      }
    });
  }

  uploadCover(): void {
    if (!this.newCoverFile) return;
    this.apis.uploadDashboardStoryCover(this.storyId, this.newCoverFile).subscribe({
      next: (res: any) => {
        this.toastr.success('Cover updated');
        this.coverUrl = res?.data?.cover || this.coverUrl;
        this.newCoverFile = null;
      },
      error: () => this.toastr.error('Failed to update cover')
    });
  }

  uploadImages(): void {
    if (!this.newImagesFiles || this.newImagesFiles.length === 0) return;
    this.apis.uploadDashboardStoryImages(this.storyId, this.newImagesFiles).subscribe({
      next: (res: any) => {
        this.toastr.success('Images added');
        const added = res?.data || [];
        this.images = this.images.concat(added).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
        this.newImagesFiles = [];
      },
      error: () => this.toastr.error('Failed to add images')
    });
  }

  back(): void {
    this.router.navigate(['/stories']);
  }
}
